import { RefObject, useLayoutEffect, useState } from 'react';

export function useDimensions<T extends HTMLElement>(
  reference: RefObject<T | null>,
) {
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (!reference.current) {
      return;
    }

    const measure = () => {
      if (!reference.current) {
        return;
      }

      const rect = reference.current.getBoundingClientRect();
      // Bail out on unchanged values: the ResizeObserver fires on every frame of
      // a resize drag, and a fresh object would re-render every consumer even
      // when the measured box did not move.
      setDimensions((previous) =>
        previous && previous.width === rect.width && previous.height === rect.height
          ? previous
          : { width: rect.width, height: rect.height },
      );
    };

    measure();

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(measure);
    });

    observer.observe(reference.current, { box: 'border-box' });

    return () => {
      observer.disconnect();
    };
  }, [reference]);

  return [dimensions];
}
